import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { GetScoutConversationHistoryQuery } from "generated-graphql/graphql";
import { usePageTitle } from "hooks/usePageTitle";
import { useToken } from "providers/token";
import { FormEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { parseSseFrame, parseStreamEventData } from "../../../stream/streamIterator";

const GET_SCOUT_CONVERSATION_HISTORY = gql(`
  query GetScoutConversationHistory($tenantId: ID!, $conversationId: ID!) {
    getConversationHistory(tenantId: $tenantId, conversationId: $conversationId) {
      conversationId
      title
      messages {
        id
        role
        seq
        lastStreamEntryId
        requestId
        createdAt
        updatedAt
        parts {
          id
          seq
          type
          streamPartId
          stepIndex
          agentState
          text
          toolName
          toolDisplayName
          toolCallId
          toolState
          toolInput
          toolOutput
          toolError
          toolTokenEstimate
          toolDurationMs
          sourceId
          url
          title
          mediaType
          filename
          createdAt
        }
      }
    }
  }
`);

const SEND_SCOUT_MESSAGE = gql(`
  mutation SendScoutMessage($input: SendScoutMessageInput!) {
    sendMessage(input: $input) {
      conversationId
      messageId
      requestId
      title
    }
  }
`);

const ABORT_SCOUT_TURN = gql(`
  mutation AbortScoutTurn($input: AbortScoutTurnInput!) {
    abortTurn(input: $input) {
      conversationId
      requestId
      success
    }
  }
`);

type StreamStatus = "idle" | "connecting" | "connected" | "closed" | "error";

type ScoutStreamEventRecord = {
  id: string;
  event: string;
  data: unknown;
};

type ScoutHistoryMessage =
  GetScoutConversationHistoryQuery["getConversationHistory"]["messages"][number];
type ScoutHistoryMessagePart = ScoutHistoryMessage["parts"][number];

type LiveScoutTurnStatus = "idle" | "streaming" | "finished" | "aborted" | "error";

type LiveScoutTurn = {
  requestId?: string;
  status: LiveScoutTurnStatus;
  text: string;
  reasoning: string;
  activities: string[];
  lastEventType?: string;
};

type StreamConnection = {
  conversationId?: string;
  nonce: number;
};

const MAX_RAW_EVENTS = 100;
const CHAT_PANEL_HEIGHT = 640;
const RAW_EVENTS_PANEL_HEIGHT = 800;

function getApiBaseUrl() {
  return (
    import.meta.env.VITE_API_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:4000" : "")
  );
}

// SSE endpoints (/events/*) are served from a dedicated CloudFront
// distribution because API Gateway buffers `text/event-stream` responses.
// Falls back to the regular API base URL for local dev where SSE goes
// straight to the express server.
function getSseBaseUrl() {
  return import.meta.env.VITE_SSE_API_URL || getApiBaseUrl();
}

// Mirrors helpers from encamp-scout-connection/event-utils.
// Oak cannot depend on that package directly because its barrel export
// re-exports Prisma/Valkey modules that are not browser-safe.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readOptionalString(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readScoutRequestId(data: unknown) {
  if (!isRecord(data)) {
    return undefined;
  }

  return readOptionalString(data, "requestId");
}

function readScoutReason(data: unknown) {
  if (!isRecord(data)) {
    return undefined;
  }

  return readOptionalString(data, "reason");
}

function readScoutPart(data: unknown) {
  if (!isRecord(data)) {
    return undefined;
  }

  const part = data.part;
  return isRecord(part) ? part : undefined;
}

function readTextDeltaText(part: Record<string, unknown>) {
  return readOptionalString(part, "textDelta") ?? readOptionalString(part, "text") ?? "";
}

function readReasoningDeltaText(part: Record<string, unknown>) {
  return readOptionalString(part, "text") ?? "";
}

function appendScoutActivity(previousActivities: string[], nextActivity: string) {
  return [...previousActivities, nextActivity].slice(-8);
}

function createEmptyLiveScoutTurn(): LiveScoutTurn {
  return {
    status: "idle",
    text: "",
    reasoning: "",
    activities: [],
  };
}

function createPendingLiveScoutTurn(requestId: string): LiveScoutTurn {
  return {
    requestId,
    status: "streaming",
    text: "",
    reasoning: "",
    activities: [],
  };
}

function formatLiveScoutActivity(
  streamEvent: ScoutStreamEventRecord,
  part: Record<string, unknown> | undefined
) {
  const toolLabel = part
    ? (readOptionalString(part, "toolDisplayName") ?? readOptionalString(part, "toolName"))
    : undefined;

  switch (streamEvent.event) {
    case "start":
      return "Turn started";
    case "finish":
      return "Turn finished";
    case "error":
      return "Turn error";
    case "abort":
      return "Turn aborted";
    case "turn_aborted": {
      const reason = readScoutReason(streamEvent.data);
      return reason ? `Turn aborted: ${reason}` : "Turn aborted";
    }
    case "tool-input-start": {
      return toolLabel ? `Preparing tool: ${toolLabel}` : "Preparing tool";
    }
    case "tool-call": {
      return toolLabel ? `Running tool: ${toolLabel}` : "Running tool";
    }
    case "tool-result": {
      return toolLabel ? `Tool result: ${toolLabel}` : "Tool result received";
    }
    case "tool-error": {
      const toolError = part ? readOptionalString(part, "error") : undefined;
      if (toolLabel && toolError) {
        return `Tool error: ${toolLabel} (${toolError})`;
      }
      if (toolLabel) {
        return `Tool error: ${toolLabel}`;
      }
      return "Tool error";
    }
    case "source": {
      const title = part ? readOptionalString(part, "title") : undefined;
      const url = part ? readOptionalString(part, "url") : undefined;
      const sourceId = part ? readOptionalString(part, "sourceId") : undefined;
      return `Source: ${title ?? url ?? sourceId ?? "attached source"}`;
    }
    case "file": {
      const filename = part ? readOptionalString(part, "filename") : undefined;
      const mediaType = part ? readOptionalString(part, "mediaType") : undefined;
      return `File: ${filename ?? mediaType ?? "attachment"}`;
    }
    default:
      return undefined;
  }
}

function reduceLiveScoutTurn(previousTurn: LiveScoutTurn, streamEvent: ScoutStreamEventRecord) {
  const requestId = readScoutRequestId(streamEvent.data);
  const part = readScoutPart(streamEvent.data);
  const shouldResetRequest = requestId && previousTurn.requestId !== requestId;
  const nextTurn = shouldResetRequest
    ? createPendingLiveScoutTurn(requestId)
    : {
        ...previousTurn,
      };

  nextTurn.requestId = requestId ?? nextTurn.requestId;
  nextTurn.lastEventType = streamEvent.event;

  const activity = formatLiveScoutActivity(streamEvent, part);
  if (activity) {
    nextTurn.activities = appendScoutActivity(nextTurn.activities, activity);
  }

  if (streamEvent.event === "text-delta" && part) {
    nextTurn.text = `${nextTurn.text}${readTextDeltaText(part)}`;
    nextTurn.status = "streaming";
    return nextTurn;
  }

  if (streamEvent.event === "reasoning-delta" && part) {
    nextTurn.reasoning = `${nextTurn.reasoning}${readReasoningDeltaText(part)}`;
    nextTurn.status = "streaming";
    return nextTurn;
  }

  if (streamEvent.event === "finish") {
    nextTurn.status = "finished";
    return nextTurn;
  }

  if (streamEvent.event === "error") {
    nextTurn.status = "error";
    return nextTurn;
  }

  if (streamEvent.event === "abort" || streamEvent.event === "turn_aborted") {
    nextTurn.status = "aborted";
    return nextTurn;
  }

  if (STREAMING_SCOUT_EVENTS.has(streamEvent.event)) {
    nextTurn.status = "streaming";
    return nextTurn;
  }

  return nextTurn;
}

const STREAMING_SCOUT_EVENTS = new Set([
  "start",
  "start-step",
  "finish-step",
  "tool-input-start",
  "tool-input-delta",
  "tool-input-end",
  "tool-call",
  "tool-result",
  "source",
  "file",
  "raw",
]);

// Mirrors SCOUT_TERMINAL_STREAM_EVENT_TYPES from encamp-scout-connection/types.
const TERMINAL_SCOUT_EVENTS = new Set(["finish", "error", "abort", "turn_aborted"]);

function isTerminalScoutEvent(eventName: string) {
  return TERMINAL_SCOUT_EVENTS.has(eventName);
}

function formatScoutStreamStatus(status: StreamStatus, notice: string | null) {
  if (notice) {
    return notice;
  }

  if (status === "idle") {
    return "Waiting for a conversation.";
  }

  if (status === "connecting") {
    return "Connecting to Scout SSE...";
  }

  if (status === "connected") {
    return "Connected to Scout SSE.";
  }

  if (status === "closed") {
    return "Scout SSE connection closed.";
  }

  return "Scout SSE connection failed.";
}

function formatTimestamp(isoString: string) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return isoString;
  }

  return date.toLocaleString();
}

function formatMessageRole(role: string) {
  if (role.length === 0) {
    return "Unknown";
  }

  return `${role.slice(0, 1).toUpperCase()}${role.slice(1)}`;
}

function formatMessagePartSummary(part: ScoutHistoryMessagePart) {
  if (part.type === "tool") {
    const toolName = part.toolDisplayName ?? part.toolName ?? "unknown tool";
    const toolState = part.toolState ? ` (${part.toolState})` : "";
    return `Tool: ${toolName}${toolState}`;
  }

  if (part.type === "source") {
    return `Source${part.title ? `: ${part.title}` : ""}`;
  }

  if (part.type === "file") {
    return `File${part.filename ? `: ${part.filename}` : ""}`;
  }

  if (part.type === "reasoning") {
    return "Reasoning";
  }

  if (part.type === "abort") {
    return "Abort";
  }

  if (part.type === "text") {
    return "Text";
  }

  return part.type;
}

function formatMessagePartMeta(part: ScoutHistoryMessagePart) {
  const metadata: string[] = [];

  if (part.stepIndex !== null && part.stepIndex !== undefined) {
    metadata.push(`step ${part.stepIndex}`);
  }

  if (part.agentState) {
    metadata.push(part.agentState);
  }

  if (part.toolDurationMs !== null && part.toolDurationMs !== undefined) {
    metadata.push(`${part.toolDurationMs} ms`);
  }

  if (part.toolTokenEstimate !== null && part.toolTokenEstimate !== undefined) {
    metadata.push(`${part.toolTokenEstimate} tokens`);
  }

  return metadata.join(" | ");
}

const JsonBlock = memo(function JsonBlock(props: { title?: string; value: unknown }) {
  const serializedValue = useMemo(() => JSON.stringify(props.value, null, 2), [props.value]);

  return (
    <Box sx={{ mt: 1 }}>
      {props.title ? (
        <Typography color="text.secondary" variant="caption">
          {props.title}
        </Typography>
      ) : null}
      <Box
        component="pre"
        sx={{
          m: 0,
          mt: props.title ? 0.5 : 0,
          p: 1.5,
          overflowX: "auto",
          borderRadius: 1,
          backgroundColor: "action.hover",
          color: "text.primary",
          whiteSpace: "pre-wrap",
        }}
      >
        {serializedValue}
      </Box>
    </Box>
  );
});

const ScoutMessagePartView = memo(function ScoutMessagePartView(props: {
  part: ScoutHistoryMessagePart;
}) {
  const { part } = props;
  const summary = formatMessagePartSummary(part);
  const metadata = formatMessagePartMeta(part);

  return (
    <Box
      sx={{
        borderRadius: 1,
        p: part.type === "text" ? 0 : 1.5,
        backgroundColor: part.type === "text" ? "transparent" : "action.hover",
      }}
    >
      <Typography color="text.secondary" variant="caption">
        {summary}
        {metadata ? ` | ${metadata}` : ""}
      </Typography>

      {part.text ? (
        <Typography sx={{ mt: 0.5, whiteSpace: "pre-wrap" }} variant="body2">
          {part.text}
        </Typography>
      ) : null}

      {part.url ? (
        <Box sx={{ mt: 0.5 }}>
          <Link href={part.url} rel="noreferrer" target="_blank" variant="body2">
            {part.title ?? part.url}
          </Link>
        </Box>
      ) : null}

      {part.toolInput !== null && part.toolInput !== undefined ? (
        <JsonBlock title="Tool input" value={part.toolInput} />
      ) : null}

      {part.toolOutput !== null && part.toolOutput !== undefined ? (
        <JsonBlock title="Tool output" value={part.toolOutput} />
      ) : null}

      {part.toolError ? (
        <Typography color="error" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }} variant="body2">
          {part.toolError}
        </Typography>
      ) : null}
    </Box>
  );
});

const ScoutMessageCard = memo(function ScoutMessageCard(props: { message: ScoutHistoryMessage }) {
  const { message } = props;
  const isUserMessage = message.role.toLowerCase() === "user";

  return (
    <Paper
      sx={{
        alignSelf: isUserMessage ? "flex-end" : "stretch",
        border: 1,
        borderColor: "divider",
        maxWidth: isUserMessage ? 720 : "100%",
        p: 2,
        width: "100%",
      }}
      variant="outlined"
    >
      <Stack spacing={1}>
        <Typography color="text.secondary" variant="caption">
          {formatMessageRole(message.role)} | seq {message.seq} |{" "}
          {formatTimestamp(message.createdAt)}
        </Typography>

        {message.parts.map((part) => (
          <ScoutMessagePartView key={part.id} part={part} />
        ))}
      </Stack>
    </Paper>
  );
});

const LiveScoutTurnCard = memo(function LiveScoutTurnCard(props: { liveTurn: LiveScoutTurn }) {
  const { liveTurn } = props;

  if (
    !liveTurn.requestId &&
    liveTurn.text.length === 0 &&
    liveTurn.reasoning.length === 0 &&
    liveTurn.activities.length === 0
  ) {
    return null;
  }

  return (
    <Paper
      sx={{
        border: 1,
        borderColor: "divider",
        p: 2,
      }}
      variant="outlined"
    >
      <Stack spacing={1}>
        <Typography color="text.secondary" variant="caption">
          Live Scout turn
          {liveTurn.requestId ? ` | request ${liveTurn.requestId}` : ""}
          {liveTurn.lastEventType ? ` | last event ${liveTurn.lastEventType}` : ""}
          {` | ${liveTurn.status}`}
        </Typography>

        {liveTurn.text ? (
          <Typography sx={{ whiteSpace: "pre-wrap" }} variant="body2">
            {liveTurn.text}
          </Typography>
        ) : (
          <Typography color="text.secondary" variant="body2">
            Awaiting assistant text...
          </Typography>
        )}

        {liveTurn.reasoning ? (
          <JsonBlock title="Reasoning delta" value={liveTurn.reasoning} />
        ) : null}

        {liveTurn.activities.length > 0 ? (
          <Stack spacing={0.5}>
            {liveTurn.activities.map((activity, index) => (
              <Typography key={`${activity}-${index}`} color="text.secondary" variant="body2">
                {activity}
              </Typography>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
});

type ScoutChatPanelProps = {
  conversationId?: string;
  errorMessage?: string;
  historyLoading: boolean;
  isSending: boolean;
  isTurnRunning: boolean;
  liveTurn: LiveScoutTurn;
  messages: ScoutHistoryMessage[];
  onSendMessage: (content: string) => Promise<boolean>;
};

const ScoutChatPanel = memo(function ScoutChatPanel(props: ScoutChatPanelProps) {
  const {
    conversationId,
    errorMessage,
    historyLoading,
    isSending,
    isTurnRunning,
    liveTurn,
    messages,
    onSendMessage,
  } = props;
  const [draftMessage, setDraftMessage] = useState("");

  useEffect(() => {
    setDraftMessage("");
  }, [conversationId]);

  const handleSend = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const content = draftMessage.trim();
      if (content.length === 0) {
        return;
      }

      const didSend = await onSendMessage(content);
      if (didSend) {
        setDraftMessage("");
      }
    },
    [draftMessage, onSendMessage]
  );

  const transcriptContent = useMemo(() => {
    if (!conversationId) {
      return (
        <Alert severity="info">
          Send a message to create a Scout conversation for this tenant.
        </Alert>
      );
    }

    if (historyLoading && messages.length === 0) {
      return (
        <Stack alignItems="center" direction="row" spacing={1}>
          <CircularProgress size={18} />
          <Typography color="text.secondary" variant="body2">
            Loading Scout conversation history...
          </Typography>
        </Stack>
      );
    }

    if (messages.length === 0 && !liveTurn.requestId) {
      return (
        <Alert severity="info">
          No Scout messages have been persisted for this conversation yet.
        </Alert>
      );
    }

    return (
      <Stack spacing={2}>
        {messages.map((message) => (
          <ScoutMessageCard key={message.id} message={message} />
        ))}
        <LiveScoutTurnCard liveTurn={liveTurn} />
      </Stack>
    );
  }, [conversationId, historyLoading, liveTurn, messages]);

  return (
    <Paper
      sx={{
        border: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: CHAT_PANEL_HEIGHT,
        minWidth: 0,
        p: 2,
      }}
      variant="outlined"
    >
      <Stack sx={{ height: "100%", minHeight: 0 }} spacing={2}>
        <Typography variant="h6">Scout Chat</Typography>

        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>{transcriptContent}</Box>

        <Divider />

        <Box component="form" onSubmit={handleSend}>
          <Stack spacing={1.5}>
            <TextField
              disabled={isSending}
              label="Message"
              minRows={3}
              multiline
              onChange={(nextEvent) => {
                setDraftMessage(nextEvent.target.value);
              }}
              placeholder="Ask Scout something for this tenant..."
              value={draftMessage}
            />
            <Stack direction={{ lg: "row", xs: "column" }} spacing={1}>
              <Button
                disabled={draftMessage.trim().length === 0 || isSending || isTurnRunning}
                type="submit"
                variant="contained"
              >
                {isSending ? "Sending..." : "Send"}
              </Button>
              <Typography color="text.secondary" variant="body2">
                This is a basic diagnostics surface, so live event details stay visible in the log
                while durable history is refreshed from GraphQL.
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
});

type RawScoutEventsPanelProps = {
  error: string | null;
  notice: string | null;
  rawEvents: ScoutStreamEventRecord[];
};

const RawScoutEventsPanel = memo(function RawScoutEventsPanel(props: RawScoutEventsPanelProps) {
  const { error, notice, rawEvents } = props;

  return (
    <Paper
      sx={{
        border: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: RAW_EVENTS_PANEL_HEIGHT,
        minWidth: 0,
        p: 2,
      }}
      variant="outlined"
    >
      <Stack sx={{ height: "100%", minHeight: 0 }} spacing={2}>
        <Typography variant="h6">Raw Scout Events</Typography>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {!error && notice ? <Alert severity="info">{notice}</Alert> : null}

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 1 }}>
          {rawEvents.length === 0 ? (
            <Typography color="text.secondary">
              No Scout SSE events received in this session yet.
            </Typography>
          ) : (
            <List disablePadding>
              {rawEvents.map((streamEvent, index) => (
                <Box key={`${streamEvent.id}-${index}`}>
                  <ListItem alignItems="flex-start" disableGutters>
                    <ListItemText
                      primary={`${streamEvent.event} (${streamEvent.id})`}
                      secondaryTypographyProps={{ component: "div" }}
                      secondary={<JsonBlock value={streamEvent.data} />}
                    />
                  </ListItem>
                  {index < rawEvents.length - 1 ? <Divider /> : null}
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Stack>
    </Paper>
  );
});

function useScoutSseStream(
  tenantId: string | undefined,
  connection: StreamConnection,
  getLastEventId: () => string | undefined,
  onEventId: (eventId: string) => void,
  onStreamEvent: (streamEvent: ScoutStreamEventRecord) => void
) {
  const { getIdTokenRefreshIfExpired } = useToken();
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const onEventIdRef = useRef(onEventId);
  const onStreamEventRef = useRef(onStreamEvent);
  const getLastEventIdRef = useRef(getLastEventId);

  useEffect(() => {
    onEventIdRef.current = onEventId;
  }, [onEventId]);

  useEffect(() => {
    onStreamEventRef.current = onStreamEvent;
  }, [onStreamEvent]);

  useEffect(() => {
    getLastEventIdRef.current = getLastEventId;
  }, [getLastEventId]);

  useEffect(() => {
    if (!tenantId || !connection.conversationId || connection.nonce === 0) {
      setStatus("idle");
      setError(null);
      setNotice(null);
      return;
    }

    const abortController = new AbortController();
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;

    setStatus("connecting");
    setError(null);
    setNotice(null);

    const appendEvent = (id: string, event: string, rawData: string) => {
      const streamEvent = {
        id,
        event,
        data: parseStreamEventData(rawData),
      };

      onStreamEventRef.current(streamEvent);
      onEventIdRef.current(id);
    };

    const processFrame = (frame: string) => {
      const parsedFrame = parseSseFrame(frame);
      if (!parsedFrame || !parsedFrame.id) {
        return;
      }

      appendEvent(parsedFrame.id, parsedFrame.event, parsedFrame.rawData);
    };

    const consumeStream = async () => {
      try {
        const idToken = await getIdTokenRefreshIfExpired();
        const headers: Record<string, string> = {
          accept: "text/event-stream",
          authorization: `Bearer ${idToken || ""}`,
        };
        const lastEventId = getLastEventIdRef.current();

        if (lastEventId) {
          headers["Last-Event-ID"] = lastEventId;
        }

        const response = await fetch(
          `${getSseBaseUrl()}/events/${tenantId}/scout/${connection.conversationId}`,
          {
            headers,
            signal: abortController.signal,
          }
        );

        if (response.status === 409) {
          setStatus("idle");
          setNotice("No active Scout turn is streaming right now.");
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to connect to Scout SSE stream (${response.status}).`);
        }

        if (!response.body) {
          throw new Error("Scout SSE response body was empty.");
        }

        setStatus("connected");
        reader = response.body.getReader();

        const decoder = new TextDecoder();
        let bufferedChunk = "";

        while (!abortController.signal.aborted) {
          const { done, value } = await reader.read();

          if (done) {
            const trailingChunk = `${bufferedChunk}${decoder.decode()}`.trim();
            if (trailingChunk) {
              processFrame(trailingChunk);
            }
            break;
          }

          bufferedChunk += decoder.decode(value, { stream: true });
          const frames = bufferedChunk.split(/\r?\n\r?\n/);
          bufferedChunk = frames.pop() ?? "";

          for (const frame of frames) {
            processFrame(frame);
          }
        }

        if (!abortController.signal.aborted) {
          setStatus("closed");
        }
      } catch (streamError) {
        if (abortController.signal.aborted) {
          return;
        }

        setStatus("error");
        setError(
          streamError instanceof Error
            ? streamError.message
            : "Failed to connect to Scout SSE stream."
        );
      }
    };

    void consumeStream();

    return () => {
      abortController.abort();
      if (reader) {
        void reader.cancel().catch(() => undefined);
      }
    };
  }, [connection.conversationId, connection.nonce, getIdTokenRefreshIfExpired, tenantId]);

  return {
    status: tenantId ? status : "error",
    error: tenantId ? error : "Missing tenant id.",
    notice,
  };
}

export function Sse() {
  usePageTitle("Scout SSE");

  const { tenantId } = useParams();
  const alerts = useAlerts();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lastEventId, setLastEventId] = useState(searchParams.get("lastEventId") ?? undefined);
  const [rawEvents, setRawEvents] = useState<ScoutStreamEventRecord[]>([]);
  const [liveTurn, setLiveTurn] = useState<LiveScoutTurn>(() => createEmptyLiveScoutTurn());
  const [streamConnection, setStreamConnection] = useState<StreamConnection>({
    conversationId: undefined,
    nonce: 0,
  });
  const latestSearchRef = useRef(location.search);
  const resumeFromEventIdRef = useRef<string | undefined>(
    searchParams.get("lastEventId") ?? undefined
  );
  const previousConversationIdRef = useRef<string | undefined>(undefined);
  const lastTerminalRequestIdRef = useRef<string | undefined>(undefined);
  const conversationId = searchParams.get("conversationId") ?? undefined;

  const {
    data,
    error: historyError,
    loading: historyLoading,
    refetch: refetchHistoryQuery,
  } = useQuery(GET_SCOUT_CONVERSATION_HISTORY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    skip: !tenantId || !conversationId,
    variables:
      tenantId && conversationId
        ? {
            tenantId,
            conversationId,
          }
        : undefined,
  });

  const [sendScoutMessage, { loading: isSending }] = useMutation(SEND_SCOUT_MESSAGE);
  const [abortScoutTurn, { loading: isAborting }] = useMutation(ABORT_SCOUT_TURN);

  useEffect(() => {
    latestSearchRef.current = location.search;
  }, [location.search]);

  const updateScoutSearchParams = useCallback(
    (updates: { conversationId?: string | null; lastEventId?: string | null }) => {
      const nextSearchParams = new URLSearchParams(latestSearchRef.current);

      if (updates.conversationId !== undefined) {
        if (updates.conversationId) {
          nextSearchParams.set("conversationId", updates.conversationId);
        } else {
          nextSearchParams.delete("conversationId");
        }
      }

      if (updates.lastEventId !== undefined) {
        if (updates.lastEventId) {
          nextSearchParams.set("lastEventId", updates.lastEventId);
        } else {
          nextSearchParams.delete("lastEventId");
        }
      }

      setSearchParams(nextSearchParams, { replace: true });
    },
    [setSearchParams]
  );

  const handleEventId = useCallback((eventId: string) => {
    if (resumeFromEventIdRef.current === eventId) {
      return;
    }

    resumeFromEventIdRef.current = eventId;
    setLastEventId((previousEventId) => (previousEventId === eventId ? previousEventId : eventId));
  }, []);

  const refetchHistory = useCallback(async () => {
    if (!tenantId || !conversationId) {
      return;
    }

    await refetchHistoryQuery({
      conversationId,
      tenantId,
    });
  }, [conversationId, refetchHistoryQuery, tenantId]);

  const startScoutStream = useCallback(
    (targetConversationId?: string) => {
      const nextConversationId = targetConversationId ?? conversationId;
      if (!nextConversationId) {
        return;
      }

      resumeFromEventIdRef.current = lastEventId;
      setStreamConnection((previousConnection) => ({
        conversationId: nextConversationId,
        nonce: previousConnection.nonce + 1,
      }));
    },
    [conversationId, lastEventId]
  );

  const handleStreamEvent = useCallback(
    (streamEvent: ScoutStreamEventRecord) => {
      setRawEvents((previousEvents) => [...previousEvents, streamEvent].slice(-MAX_RAW_EVENTS));
      setLiveTurn((previousTurn) => reduceLiveScoutTurn(previousTurn, streamEvent));

      const requestId = readScoutRequestId(streamEvent.data);
      if (requestId && isTerminalScoutEvent(streamEvent.event)) {
        if (lastTerminalRequestIdRef.current === requestId) {
          return;
        }

        lastTerminalRequestIdRef.current = requestId;
        void refetchHistory().catch(() => undefined);
      }
    },
    [refetchHistory]
  );

  const { status, error, notice } = useScoutSseStream(
    tenantId,
    streamConnection,
    () => resumeFromEventIdRef.current,
    handleEventId,
    handleStreamEvent
  );

  const resetSessionState = useCallback(
    (nextConversationId: string | undefined, nextLastEventId: string | undefined) => {
      previousConversationIdRef.current = nextConversationId;
      lastTerminalRequestIdRef.current = undefined;
      resumeFromEventIdRef.current = nextLastEventId;
      setLastEventId(nextLastEventId);
      setRawEvents([]);
      setLiveTurn(createEmptyLiveScoutTurn());
      setStreamConnection((previousConnection) => ({
        conversationId: nextConversationId,
        nonce: previousConnection.nonce + 1,
      }));
    },
    []
  );

  useEffect(() => {
    if (!conversationId) {
      resetSessionState(undefined, undefined);
      return;
    }

    if (previousConversationIdRef.current === conversationId) {
      return;
    }

    const savedEventId =
      new URLSearchParams(latestSearchRef.current).get("lastEventId") ?? undefined;
    resetSessionState(conversationId, savedEventId);
  }, [conversationId, resetSessionState]);

  const historyCandidate = data?.getConversationHistory;
  const history =
    conversationId && historyCandidate?.conversationId === conversationId
      ? historyCandidate
      : undefined;
  const messages = history?.messages ?? [];
  const effectiveHistoryError = conversationId ? historyError : undefined;
  const isTurnRunning = liveTurn.status === "streaming";

  const handleSend = useCallback(
    async (content: string) => {
      if (!tenantId || content.length === 0) {
        return false;
      }

      try {
        const result = await sendScoutMessage({
          variables: {
            input: {
              tenantId,
              conversationId,
              content,
            },
          },
        });
        const sendResult = result.data?.sendMessage;

        if (!sendResult) {
          return false;
        }

        lastTerminalRequestIdRef.current = undefined;
        setLastEventId(undefined);
        setLiveTurn(createPendingLiveScoutTurn(sendResult.requestId));

        if (conversationId !== sendResult.conversationId) {
          resumeFromEventIdRef.current = undefined;
          updateScoutSearchParams({
            conversationId: sendResult.conversationId,
            lastEventId: null,
          });
        } else {
          startScoutStream();
          await refetchHistory().catch(() => undefined);
        }

        return true;
      } catch (sendError) {
        alerts.error("Failed to send Scout message", sendError);
        return false;
      }
    },
    [
      alerts,
      conversationId,
      refetchHistory,
      sendScoutMessage,
      startScoutStream,
      tenantId,
      updateScoutSearchParams,
    ]
  );

  const handleAbort = useCallback(async () => {
    if (!tenantId || !conversationId) {
      return;
    }

    try {
      const result = await abortScoutTurn({
        variables: {
          input: {
            tenantId,
            conversationId,
            reason: "Aborted from Staff SSE diagnostics",
          },
        },
      });
      const abortResult = result.data?.abortTurn;

      if (!abortResult?.success) {
        alerts.info("Scout turn was not active.");
        return;
      }

      alerts.success("Scout turn abort requested.");
      setLiveTurn((previousTurn) => ({
        ...previousTurn,
        status: "aborted",
      }));
      await refetchHistory().catch(() => undefined);
    } catch (abortError) {
      alerts.error("Failed to abort Scout turn", abortError);
    }
  }, [abortScoutTurn, alerts, conversationId, refetchHistory, tenantId]);

  const handleNewConversation = useCallback(() => {
    resetSessionState(undefined, undefined);
    updateScoutSearchParams({
      conversationId: null,
      lastEventId: null,
    });
  }, [resetSessionState, updateScoutSearchParams]);

  const streamStatusMessage = formatScoutStreamStatus(status, notice);
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4">Scout SSE Diagnostics</Typography>
            <Typography color="text.secondary">
              End-to-end Scout chat diagnostics for tenant {tenantId ?? "unknown"}.
            </Typography>
          </Box>

          <Paper sx={{ border: 1, borderColor: "divider", p: 2 }} variant="outlined">
            <Stack spacing={1.5}>
              <Typography variant="h6">Conversation</Typography>
              <Typography variant="body2">Stream status: {streamStatusMessage}</Typography>
              <Typography variant="body2">
                Conversation ID: {conversationId ?? "not started"}
              </Typography>
              <Typography variant="body2">
                Last event ID: {lastEventId ?? "not received yet"}
              </Typography>
              <Stack direction={{ lg: "row", xs: "column" }} spacing={1}>
                <Button onClick={handleNewConversation} type="button" variant="outlined">
                  New conversation
                </Button>
                <Button
                  disabled={!conversationId}
                  onClick={() => {
                    startScoutStream();
                  }}
                  type="button"
                  variant="outlined"
                >
                  Reconnect stream
                </Button>
                <Button
                  color="warning"
                  disabled={!conversationId || !isTurnRunning || isAborting}
                  onClick={handleAbort}
                  type="button"
                  variant="outlined"
                >
                  {isAborting ? "Aborting..." : "Abort"}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Stack spacing={2}>
            <ScoutChatPanel
              conversationId={conversationId}
              errorMessage={effectiveHistoryError?.message}
              historyLoading={historyLoading}
              isSending={isSending}
              isTurnRunning={isTurnRunning}
              liveTurn={liveTurn}
              messages={messages}
              onSendMessage={handleSend}
            />

            <RawScoutEventsPanel error={error} notice={notice} rawEvents={rawEvents} />
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
